import { camelToSnake, snakeToCamel } from "caseparser";

import { api } from "@/app/app.api";

import { RequestMethod } from "@/shared/utils/RequestMethod";

export const nutritionApi = api
  .enhanceEndpoints({ addTagTypes: ["nutritions", "nutritionProgress", "nutritionProduct"] })
  .injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      createDishNutrition: builder.mutation({
        query: ({ dish, image, branchId }) => {
          const formData = new FormData();
          formData.append("branch_id", branchId);
          formData.append("image", image);
          Object.entries(camelToSnake(dish.gallery)).forEach(([key, value]) => {
            formData.append(`gallery[${key}]`, value);
          });

          // Products
          camelToSnake(dish.products).forEach((product, index) => {
            Object.entries(product).forEach(([key, value]) => {
              formData.append(`products[${index}][${key}]`, value);
            });
          });
          return {
            url: "/nutrition/dishes",
            method: RequestMethod.POST,
            body: formData,
          };
        },
      }),
      createProductNutrition: builder.mutation({
        query: ({ product, equivalences, nutritionalInformation, carts }) => ({
          url: "/nutrition/products",
          method: RequestMethod.POST,
          body: camelToSnake({
            product,
            equivalences,
            nutritionalInformation,
            carts,
          }),
        }),
        invalidatesTags: ["nutritionProduct"],
      }),
      updateProductNutrition: builder.mutation({
        query: ({ product, equivalences, nutritionalInformation, carts }) => ({
          url: "/nutrition/products",
          method: RequestMethod.PATCH,
          body: camelToSnake({
            product,
            equivalences,
            nutritionalInformation,
            carts,
          }),
        }),
        invalidatesTags: ["nutritionProduct"],
      }),
      productPaginator: builder.query({
        query: ({ page, product }) => ({
          method: RequestMethod.GET,
          url: "/nutrition/products/paginator",
          params: {
            page,
            product
          },
        }),
        transformResponse: (response: any) => snakeToCamel(response?.data),
        providesTags: ["nutritionProduct"]
      }),
      deleteNutrition: builder.mutation({
        query: ({ id }) => ({
          method: RequestMethod.DELETE,
          url: `/nutrition-plans/${id}`,
        }),
        transformResponse: (response: any) => snakeToCamel(response?.data),
        invalidatesTags: ["nutritions"],
      }),
      assignNutrition: builder.mutation({
        query: ({ id, userId }) => ({
          method: RequestMethod.POST,
          url: `/nutrition-plans/${id}/assign/${userId}`,
        }),
        transformResponse: (response: any) => snakeToCamel(response?.data),
        invalidatesTags: ["nutritions", "nutritionProgress"],
      }),
      duplicateNutrition: builder.mutation({
        query: ({ id, userId }) => ({
          method: RequestMethod.POST,
          url: `/nutrition-plans/${id}/duplication/${userId}`,
        }),
        transformResponse: (response: any) => snakeToCamel(response?.data),
        invalidatesTags: ["nutritions"],
      }),
      nutritions: builder.query({
        query: () => ({
          method: RequestMethod.GET,
          url: `/nutrition-plans/`,
        }),
        transformResponse: (response: any) => snakeToCamel(response?.data),
        providesTags: ["nutritions"],
      }),
      productNutritions: builder.query({
        query: ({ nutritionTable, product }) => ({
          method: "GET",
          url: "/nutrition/products",
          query: camelToSnake({
            nutritionTable,
            product,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response?.data),
      }),
      nutrition: builder.query({
        query: ({ id, userId }) => ({
          method: "GET",
          url: `/nutrition/${id}`,
          params: camelToSnake({
            userId, // user_id
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response?.data),
      }),
      nutritionEditable: builder.query({
        query: ({ id, userId }) => ({
          method: "GET",
          url: `/nutrition-editor/editable/${id}`,
          params: camelToSnake({ userId }),
        }),
        transformResponse: (response: any) => snakeToCamel(response?.data),
      }),
      nutritionOverview: builder.query({
        query: ({ id, nutritionTable, userId }) => ({
          method: "GET",
          url: `/nutrition-editor/overview/${id}`,
          params: camelToSnake({ userId, nutritionTable }),
        }),
        transformResponse: (response: any) => snakeToCamel(response?.data),
      }),
      dish: builder.query({
        query: ({ tableNutrition, nutritionId, meals, option }) => ({
          method: "GET",
          url: "/nutrition/dish",
          params: camelToSnake({
            tableNutrition,
            nutritionId,
            meals,
            option,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response?.data),
      }),
      suplementations: builder.query({
        query: ({ search }) => ({
          method: "GET",
          url: "/nutrition/suplementations",
          params: { search },
        }),
        transformResponse: (response: any) => snakeToCamel(response?.data),
      }),
      dishes: builder.query({
        query: ({ search, meal, page, objective }) => ({
          method: "GET",
          url: "/nutrition/dishes",
          params: {
            search,
            meal,
            page,
            objective
          },
        }),
        keepUnusedDataFor: 0,
        transformResponse: (response: any) => snakeToCamel(response),
      }),
      nutritionalInformations: builder.query({
        query: ({ product }) => ({
          method: "GET",
          url: "/nutrition/nutritional-informations",
          params: { product },
        }),
        transformResponse: (response: any) => snakeToCamel(response?.data),
      }),
      equivalences: builder.query({
        query: ({ product }) => ({
          method: "GET",
          url: "/nutrition/equivalences",
          params: { product },
        }),
        transformResponse: (response: any) => snakeToCamel(response?.data),
      }),
      updateNutrition: builder.mutation({
        query: ({ userId, ...body }) => ({
          url: `/nutrition`,
          method: RequestMethod.PUT,
          body: camelToSnake(body),
          params: camelToSnake({ userId }),
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        invalidatesTags: ["nutritionProgress"]
      }),
    }),
  });

export const {
  useCreateDishNutritionMutation,
  useCreateProductNutritionMutation,
  useProductPaginatorQuery,
  useProductNutritionsQuery,
  useUpdateProductNutritionMutation,
  useNutritionQuery,
  useUpdateNutritionMutation,
  useDishesQuery,
  useLazyDishQuery,
  useLazyEquivalencesQuery,
  useLazyNutritionalInformationsQuery,
  useSuplementationsQuery,
  useNutritionEditableQuery,
  useNutritionOverviewQuery,
  useLazyNutritionOverviewQuery,
  useNutritionsQuery,
  useDeleteNutritionMutation,
  useDuplicateNutritionMutation,
  useAssignNutritionMutation,
} = nutritionApi;
